/**
 * Error monitoring with the privacy rules from docs/12-accounts-and-legal.md:
 * no Session Replay, no default PII, no request/response bodies, and a scrub of
 * anything that looks like claim or personal text. Does nothing without a DSN.
 *
 * The SDK is **imported dynamically**, so it isn't part of the first load (doc 05's
 * performance budget): it's ~18KB gzipped of code that does nothing until something
 * goes wrong, and a deployment with no DSN never downloads it at all. The cost is a
 * short window at startup where a crash isn't reported, which is a fair trade for a
 * faster first paint on a phone.
 */
import type * as Sentry from '@sentry/react';

type SentrySdk = typeof Sentry;

const SCRUB_KEY = /claim|note|about|text|email|token|code/i;

function scrub(value: unknown, depth = 0): unknown {
  if (depth > 6 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1));
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([k, v]) => [
      k,
      SCRUB_KEY.test(k) ? '[scrubbed]' : scrub(v, depth + 1),
    ]),
  );
}

/** Resolves once the SDK is loaded, so a later `captureException` can wait for it. */
let loading: Promise<SentrySdk> | null = null;

export async function initSentry(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  loading = import('@sentry/react');
  const Sentry = await loading;

  Sentry.init({
    dsn,
    environment:
      (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE,
    sendDefaultPii: false,
    // Errors only for now; tracing is added with the API in M4.
    integrations: [],
    beforeBreadcrumb(breadcrumb) {
      // Never keep fetch/xhr payload details.
      if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
        return {
          ...breadcrumb,
          data: { method: breadcrumb.data?.method, status_code: breadcrumb.data?.status_code },
        };
      }
      return breadcrumb;
    },
    beforeSend(event) {
      if (event.request) {
        delete event.request.data;
        delete event.request.cookies;
        if (event.request.query_string) event.request.query_string = '[scrubbed]';
      }
      if (event.extra) event.extra = scrub(event.extra) as typeof event.extra;
      if (event.contexts) event.contexts = scrub(event.contexts) as typeof event.contexts;
      return event;
    },
  });
}

/**
 * Reports an error, whether or not the SDK has finished loading. Without a DSN this is
 * a no-op, which is also what it was before.
 */
export async function captureException(error: unknown): Promise<void> {
  if (!loading) return;
  const Sentry = await loading;
  Sentry.captureException(error);
}
